import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';

const groups = [
  {
    label: 'Build',
    items: [
      { to: '/sites', label: 'Sites', icon: '◐' },
      { to: '/templates', label: 'Templates', icon: '◇' },
    ],
  },
  {
    label: 'Grow',
    items: [
      { to: '/ecommerce', label: 'Commerce', icon: '⌬' },
      { to: '/marketing', label: 'Marketing & SEO', icon: '✶' },
      { to: '/monetization', label: 'Monetization', icon: '◈' },
      { to: '/community', label: 'Portfolio & Community', icon: '☷' },
    ],
  },
  {
    label: 'Operate',
    items: [
      { to: '/integrations', label: 'Integrations', icon: '⤬' },
      { to: '/hosting', label: 'Hosting', icon: '⌘' },
      { to: '/security', label: 'Security', icon: '◎' },
      { to: '/collaboration', label: 'Collaboration', icon: '◍' },
      { to: '/developer', label: 'Developer', icon: '⌥' },
    ],
  },
  {
    label: 'Account',
    items: [{ to: '/settings', label: 'Settings', icon: '⚙' }],
  },
];

export default function DashboardLayout() {
  const { user, workspace, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="grid min-h-screen grid-cols-[260px,1fr] bg-ink-50 text-ink-900 dark:bg-ink-950 dark:text-ink-100">
      <aside className="flex flex-col border-r border-ink-200 bg-white dark:border-ink-800 dark:bg-ink-900">
        <div className="flex items-center gap-2 px-5 py-5">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-500 text-white">
            P
          </span>
          <div className="leading-tight">
            <div className="text-sm font-bold">Pubvora</div>
            <div className="text-xs text-ink-500">{workspace?.name}</div>
          </div>
        </div>
        <nav className="scroll-pane flex-1 overflow-y-auto px-2 pb-4">
          {groups.map((g) => (
            <div key={g.label} className="mt-4">
              <div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                {g.label}
              </div>
              <ul className="mt-1">
                {g.items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      className={({ isActive }) =>
                        clsx(
                          'mx-1 mb-0.5 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition',
                          isActive
                            ? 'bg-brand-50 text-brand-700 dark:bg-ink-800 dark:text-ink-50'
                            : 'text-ink-700 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
                        )
                      }
                    >
                      <span className="w-4 text-center text-ink-400">{it.icon}</span>
                      <span>{it.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-t border-ink-200 p-3 dark:border-ink-800">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="truncate text-xs text-ink-500">{user?.email}</div>
            </div>
            <button
              type="button"
              onClick={toggle}
              className="btn-ghost h-9 w-9 p-0 text-base"
              title="Toggle theme"
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="btn-ghost mt-2 w-full justify-start text-xs"
          >
            Log out
          </button>
        </div>
      </aside>
      <main className="scroll-pane h-screen overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
